/**
 * New users land once inside their seeded demo production instead of on the
 * empty projects page. PostHog showed 27 of 29 non-activated signups reached
 * the three-card onboarding screen and clicked nothing.
 *
 * Stored per user in localStorage: a second device showing the demo once more
 * is harmless, and it avoids a backend preferences change.
 */
const demoLandingKey = (userId: string) => `rivetai:demo-landing-seen:${userId}`;

export function hasSeenDemoLanding(userId: string): boolean {
  try {
    return window.localStorage.getItem(demoLandingKey(userId)) === "1";
  } catch {
    // Blocked storage: treat as seen so we never redirect in a loop.
    return true;
  }
}

export function markDemoLandingSeen(userId: string): void {
  try {
    window.localStorage.setItem(demoLandingKey(userId), "1");
  } catch {
    // Best effort; hasSeenDemoLanding already fails closed.
  }
}

type LandingProject = {
  projectId: string;
  workspaceId?: string;
  isDemo?: boolean;
  archived?: boolean;
};

/**
 * The demo to open on a user's first visit, or null when they should see the
 * projects page as usual.
 *
 * Only a workspace owner viewing their own workspace gets the landing, and only
 * with a demo that lives in that workspace. `projects` spans every workspace
 * the user can see, so without these checks an invited member was sent from
 * the invited workspace into their personal workspace's demo.
 */
export function pickDemoLandingProject<T extends LandingProject>(params: {
  projects: T[];
  userId: string | undefined;
  canCreateProject: boolean;
  workspaceId: string | undefined;
  isWorkspaceOwner: boolean;
}): T | null {
  const { projects, userId, canCreateProject, workspaceId, isWorkspaceOwner } = params;
  if (!userId || !canCreateProject || !workspaceId || !isWorkspaceOwner) return null;
  if (projects.some((project) => !project.isDemo)) return null;
  const demo = projects.find(
    (project) =>
      project.isDemo && !project.archived && String(project.workspaceId) === String(workspaceId)
  );
  if (!demo) return null;
  if (hasSeenDemoLanding(userId)) return null;
  return demo;
}

/**
 * Social signups start the demo copy in the background (about 20s), so the
 * first catalog a new user sees can arrive before the demo exists. These bound
 * how long the projects page keeps checking for it.
 */
export const DEMO_WAIT_NEW_ACCOUNT_MS = 10 * 60 * 1000;
export const DEMO_WAIT_POLL_INTERVAL_MS = 3000;
export const DEMO_WAIT_MAX_MS = 60 * 1000;

function isNewAccount(createdAt: string | undefined, now: number): boolean {
  if (!createdAt) return false;
  const created = Date.parse(createdAt);
  if (Number.isNaN(created)) return false;
  return now - created < DEMO_WAIT_NEW_ACCOUNT_MS;
}

/**
 * True while a new owner's own workspace is still empty and the demo landing
 * has not run yet, so the seeded demo may still be on its way.
 */
export function shouldWaitForSeededDemo(params: {
  projects: LandingProject[];
  userId: string | undefined;
  canCreateProject: boolean;
  workspaceId: string | undefined;
  isWorkspaceOwner: boolean;
  accountCreatedAt: string | undefined;
  now?: number;
}): boolean {
  const { projects, userId, canCreateProject, workspaceId, isWorkspaceOwner, accountCreatedAt } =
    params;
  if (!userId || !canCreateProject || !workspaceId || !isWorkspaceOwner) return false;
  if (projects.length > 0) return false;
  if (hasSeenDemoLanding(userId)) return false;
  return isNewAccount(accountCreatedAt, params.now ?? Date.now());
}
